import * as React from 'react';

import {
  enumTruckSortOption,
  enumTruckStatus,
  TruckStatus,
} from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button } from '@rematter/ui-web';
import {
  DataGrid,
  DataGridColumn,
  HeaderIcons,
  Pagination,
  type PaginationProps,
  useDataGridSelection,
} from '@rematter/ui-web';
import { Badge, BadgeProps } from '@rematter/ui-web';
import {
  Content,
  FloatingToolbar,
  Footer,
  Separator,
  Toolbar,
} from '@rematter/ui-web';
import { SearchField } from '@rematter/ui-web';
import { Loading } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtom } from 'jotai';
import { PlusCircle, Trash } from 'lucide-react';

import { TruckDialog } from './TruckDialog';

import {
  $limit,
  $offset,
  $searchTerm,
} from '~/modules/transportation/_state/trucksList.state';
import { useDeleteTruckMutation } from '~/modules/transportation/hooks/mutations/useDeleteTruckMutation';
import { useTrucksQuery } from '~/modules/transportation/hooks/queries/useTrucksQuery';
import { TransportationRootLayout } from '~/modules/transportation/layouts/TransportationRootLayout';
import { TruckRecord } from '~/modules/transportation/types';
import useDebouncedValue from '~/shared/hooks/useDebounce';

const getStatusColor = (status: TruckStatus): BadgeProps['color'] => {
  switch (status) {
    case enumTruckStatus.Available:
      return 'success';
    case enumTruckStatus.InUse:
      return 'purple';
    case enumTruckStatus.Inactive:
      return 'failure';
    default:
      return 'neutral';
  }
};

export const TrucksListView = () => {
  const toast = useToast();
  const t = useTranslations();
  const [selectedRows, setSelectedRows] = React.useState<TruckRecord[]>([]);
  const [searchTerm, setSearchTerm] = useAtom($searchTerm);
  const [offset, setOffset] = useAtom($offset);
  const [limit, setLimit] = useAtom($limit);
  const [isDialogOpen, setIsDialogOpen] = React.useState(false);
  const [editTruck, setEditTruck] = React.useState<TruckRecord | null>(null);

  const debouncedSearchTerm = useDebouncedValue(searchTerm ?? '', 500);

  const { data, error, isLoading, refetch } = useTrucksQuery({
    searchTerm: debouncedSearchTerm || undefined,
    limit,
    offset,
    sorting: [enumTruckSortOption.CreatedAtDesc],
  });

  const { mutateAsync: deleteTruck } = useDeleteTruckMutation();

  // Extract data and setup selection handling
  const trucks: TruckRecord[] = data?.trucks ?? [];
  const handleSelectionChange = useDataGridSelection(trucks, setSelectedRows);

  const handlePaginationChange: PaginationProps['onChange'] = ({
    limit: newLimit,
    offset: newOffset,
  }) => {
    setLimit(newLimit);
    setOffset(newOffset);
  };

  const handleSearch = (value: string) => {
    setSearchTerm(value || undefined);
    setOffset(0);
  };

  const handleCreate = () => {
    setEditTruck(null);
    setIsDialogOpen(true);
  };

  const handleDelete = async () => {
    try {
      await Promise.all(selectedRows.map((row) => deleteTruck({ id: row.id })));
      toast.success(t.trucksView.messages.deleteSuccess(selectedRows.length));
      setSelectedRows([]);
      refetch();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : t.trucksView.messages.error,
      );
    }
  };

  const COLUMNS: DataGridColumn<TruckRecord>[] = [
    {
      key: 'status',
      header: t.trucksView.table.columns.status,
      icon: HeaderIcons.Tag,
      width: 150,
      resizable: true,
      sortable: true,
      cell: ({ row }) => (
        <Badge color={getStatusColor(row.status)} size="small">
          {t.trucksView.status[row.status]}
        </Badge>
      ),
    },
    {
      key: 'licensePlate',
      header: t.trucksView.table.columns.licensePlate,
      icon: HeaderIcons.Text,
      width: 150,
      resizable: true,
      cell: ({ row }) => row.licensePlate,
    },
    {
      key: 'model',
      header: t.trucksView.table.columns.model,
      icon: HeaderIcons.Text,
      width: 200,
      resizable: true,
      sortable: true,
      cell: ({ row }) => row.model,
    },
    {
      key: 'transportationType',
      header: t.trucksView.table.columns.transportationType,
      icon: HeaderIcons.Tags,
      width: 200,
      resizable: true,
      sortable: true,
      cell: ({ row }) => row.transportationType.name,
    },
    {
      key: 'capacity',
      header: t.trucksView.table.columns.capacity,
      icon: HeaderIcons.Weight,
      width: 150,
      resizable: true,
      sortable: true,
      cell: ({ row }) => (row.capacity ? `${row.capacity} lb` : '-'),
    },
    {
      key: 'location',
      header: t.trucksView.table.columns.location,
      icon: HeaderIcons.Address,
      grow: 1,
      resizable: true,
      cell: ({ row }) =>
        row.location
          ? `${row.location.latitude}, ${row.location.longitude}`
          : '-',
    },
  ];

  if (error) {
    return (
      <TransportationRootLayout>
        <div className="p-4 text-failure">
          {t.trucksView.content.error(error.message)}
        </div>
      </TransportationRootLayout>
    );
  }

  return (
    <TransportationRootLayout className="@TrucksListView">
      <Toolbar>
        <div className="flex items-center gap-4">
          <SearchField
            value={searchTerm ?? ''}
            onChange={handleSearch}
            placeholder={t.trucksView.toolbar.search}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button
            onPress={handleCreate}
            variant="solid"
            color="primary"
            icon={<PlusCircle />}
          >
            {t.trucksView.toolbar.create}
          </Button>
        </div>
      </Toolbar>

      <FloatingToolbar visible={selectedRows.length > 0}>
        <span className="text-sm">
          <Badge color="primary" size="small">
            {selectedRows.length}
          </Badge>{' '}
          selected
        </span>
        <Separator hideDivider />
        <Button
          variant="solid"
          color="failure"
          icon={<Trash />}
          isDisabled={selectedRows.length < 1}
          onPress={handleDelete}
        >
          {t.trucksView.toolbar.delete(selectedRows.length)}
        </Button>
      </FloatingToolbar>

      <Content>
        <Loading isLoading={isLoading}>
          <DataGrid<TruckRecord>
            columns={COLUMNS}
            dataSource={trucks}
            rowKey="id"
            aria-label={t.trucksView.table.aria}
            selectionMode="multiple"
            onSelectionChange={handleSelectionChange}
            className="w-full text-sm"
            fullWidth
          />
        </Loading>
      </Content>

      <Footer>
        <Pagination
          limit={limit}
          offset={offset}
          total={data?.total ?? 0}
          onChange={handlePaginationChange}
        />
      </Footer>

      <TruckDialog
        truck={
          editTruck
            ? {
                id: editTruck.id,
                status: editTruck.status,
                licensePlate: editTruck.licensePlate,
                model: editTruck.model,
                transportationTypeId: editTruck.transportationType.id,
                capacity: editTruck.capacity ?? undefined,
                country: editTruck.country ?? '',
                state: editTruck.state ?? '',
                defaultTrailerId: editTruck.defaultTrailer?.id,
                facilityId: editTruck.facilityId,
              }
            : undefined
        }
        isOpen={isDialogOpen}
        onClose={() => {
          setEditTruck(null);
          setIsDialogOpen(false);
        }}
        onSuccess={() => {
          setEditTruck(null);
          setIsDialogOpen(false);
          refetch();
        }}
      />
    </TransportationRootLayout>
  );
};
